import AWS from 'aws-sdk'
import { v4 as uuid } from 'uuid'
import Compressor from 'compressorjs'
import get from 'lodash/get'
import set from 'lodash/set'
import unset from 'lodash/unset'
import { singular } from 'pluralize'
import { getDynamoToolbox } from './toolbox'
import { isValid } from 'date-fns'

let S3_BUCKET
let client

export const getDynamoProvider = ({
  bucket,
  getKey = (resource, id) => {
    const collection = singular(
      resource.replace('admin/', '').replace('_', ''),
    ).toUpperCase()
    const key = {
      pk: collection,
      sk: id && id.match(/#/) ? id : `${collection}#${id}`,
    }
    return key
  },
}) => {
  S3_BUCKET = bucket

  const getClient = () => {
    client = client || getDynamoToolbox()
    return client
  }

  return {
    getOne: async (resource, params) => {
      const key = getKey(resource, params.id)
      const { Item } = await getClient().Table.get(key.pk, key)
      if (bucket) {
        return { data: signObject(Item) }
      } else {
        return { data: Item }
      }
    },

    getMany: async (resource, params) => {
      const keys = params.ids.map((id) => getKey(resource, id))
      const request = await getClient().Table.batchGet(
        keys.map((key) => getClient()[key.pk].getBatch(key)),
      )
      return request.Responses
    },

    getList: async (
      resource,
      {
        sort = {},
        filter = {},
        pagination = {},
        shouldSign = true,
        ...rest
      } = {},
    ) => {
      const { page = 1, perPage = 25 } = pagination
      const key = getKey(resource).pk
      let resp = await getClient().Table.query(key)

      const results = resp.Items.filter(getListFilter(filter))
        .sort(getListSort(sort))
        .slice((page - 1) * perPage, page * perPage)

      if (bucket && shouldSign) {
        return Promise.all(results.map(signObject)).then((data) => {
          const _data = data.flat().sort(getListSort(sort))
          return { data: _data, total: _data.length }
        })
      }

      return {
        data: results,
        total: resp.Items.filter(getListFilter(filter)).length,
      }
    },

    create: async (resource, params) => {
      if (bucket) {
        await handleFileChanges(getKey(resource).pk, params)
      }
      const sk =
        params.id || params.data.id || params.sk || params.data.sk || uuid()
      const attributes = { ...params.data, ...getKey(resource, sk), id: sk }
      await getClient()[attributes.pk].put(attributes)
      return { data: attributes }
    },

    update: async (resource, params) => {
      if (bucket) {
        await handleFileChanges(getKey(resource).pk, params)
      }
      const sk = params.sk || params.id || params.data.id
      const attributes = { ...params.data, ...getKey(resource, sk) }
      await getClient()[attributes.pk].put(attributes)
      return { data: attributes }
    },

    delete: async (resource, params) => {
      const key = getKey(resource, params.id)
      await getClient()[key.pk].delete(key)
    },

    updateMany: async (resource, params) => {
      const data = await Promise.all(
        params.ids.map((id) => {
          const attributes = { ...params.data, ...getKey(resource, id) }
          return getClient()[attributes.pk].update(attributes)
        }),
      )

      return { data }
    },

    deleteMany: async (resource, params) => {
      const data = await Promise.all(
        params.ids.map((id) => {
          const key = getKey(resource, id)
          return getClient()[key.pk].delete(key).data
        }),
      )

      return { data }
    },

    getManyReference: () => Promise,
  }
}

function getPathsToKey(object, key, path = '') {
  var values = []
  if (typeof object !== 'object') return object

  Object.keys(object).forEach(function (k) {
    const nextPath = k.match(/^\d+$/)
      ? `${path}[${k}]`
      : `${path}${path.length > 0 ? '.' : ''}${k}`
    if (k === key) {
      values.push(nextPath)
    }
    if (object[k] && typeof object[k] === 'object') {
      let result = getPathsToKey(object[k], key, nextPath)
      values = values.concat(result)
    }
  })

  return values
}

const signObject = async (object) => {
  if (typeof object !== 'object') return object

  await Promise.all(
    getPathsToKey(object, 'src').map((path) => {
      return new Promise((resolve) =>
        getSignedUrl(get(object, path)).then((signedUrl) => {
          const signedPath = path.replace('.src', '.signedSrc')
          object = set(object, signedPath, signedUrl)
          resolve(object)
        }),
      )
    }),
  )

  return object
}

const handleFileChanges = async (resource, params) => {
  // delete changed files
  if (params.previousData) {
    await Promise.all(
      getPathsToKey(params.previousData, 'src')
        .filter(
          (path) => get(params.data, path) !== get(params.previousData, path),
        )
        .map((path) =>
          new AWS.S3()
            .deleteObject({
              Bucket: S3_BUCKET,
              Key: get(params.previousData, path),
            })
            .promise(),
        ),
    )
  }

  await Promise.all(
    getPathsToKey(params.data, 'rawFile').map(async (path) => {
      const current = get(params.data, path)
      return new Promise((resolve) => {
        new Compressor(current, {
          quality: 0.65,
          maxWidth: 1200,
          convertSize: 100000,
          success: async function (result) {
            const src = `${resource}/${result.name}`.replace(/ /g, '')
            await uploadFileToBucket(src, result)
            set(params.data, path.replace('.rawFile', '.src'), src)
            unset(params.data, path)
            resolve()
          },
          error(err) {
            console.log(err)
          },
        })
      })
    }),
  )
}

export const getSignedUrl = (key) => {
  return new AWS.S3().getSignedUrlPromise('getObject', {
    Bucket: S3_BUCKET,
    Key: key,
    Expires: 604800,
  })
}

const uploadFileToBucket = (key, file) => {
  return new AWS.S3.ManagedUpload({
    params: {
      Bucket: S3_BUCKET,
      Key: key,
      Body: file,
      Metadata: {
        'Cache-Control': `max-age=${604800}`,
      },
    },
  })
    .promise()
    .then((data) => {
      return data.Location
    })
}

const getListSort = (sort) => (_a, _b) => {
  if (!sort) return 0
  const a = _a[sort.field]
  const b = _b[sort.field]
  if (!a && !b) return 0
  if (!a) return sort.order === 'ASC' ? -1 : 1
  if (!b) return sort.order === 'ASC' ? 1 : -1

  const parsedDateA = new Date(a)
  const parsedDateB = new Date(b)
  if (isValid(parsedDateA) && isValid(parsedDateB)) {
    return sort.order === 'ASC'
      ? parsedDateA - parsedDateB
      : parsedDateB - parsedDateA
  }

  if (typeof a === 'number') {
    return sort.order === 'ASC' ? a - b : b - a
  }

  if (typeof a === 'string') {
    return sort.order === 'ASC'
      ? a.localeCompare(b, undefined, { numeric: true })
      : b.localeCompare(a, undefined, { numeric: true })
  }

  if (typeof a === 'boolean') {
    return sort.order === 'ASC' ? (a ? 1 : -1) : a ? 1 : -1
  }
  return 0
}

const getListFilter = (filter) => (item) => {
  const filterKeys = Object.entries(filter)

  return filterKeys.every(([key, value]) => {
    let _value = value
    try {
      _value = JSON.parse(value)
    } catch (e) {}
    if (key === 'q' || value === undefined) {
      return true
    }
    if (key === 'archived_at') {
      return _value === 0 ? !item[key] : !!item[key]
    }
    if (Array.isArray(_value)) {
      return _value.some((v) => {
        if (v === null && !item[key]) return true
        return item[key] === v
      })
    }
    if (_value === null && !item[key]) return true

    return item[key] === _value
  })
}
